import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

/* ─── Floral corner SVG ─── */
function FloralCorner({ className }) {
  return (
    <svg
      className={`floral-corner ${className}`}
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 10 C 40 10, 60 30, 60 60 C 60 30, 80 10, 110 10 C 80 10, 60 -10, 60 -40 C 60 -10, 40 10, 10 10Z"
        fill="#c9a84c"
        transform="translate(0, 50) scale(0.8)"
      />
      <path
        d="M0 0 Q 30 60 80 80 Q 60 30 0 0"
        stroke="#c9a84c"
        strokeWidth="1.5"
        fill="none"
        transform="translate(0, 0)"
      />
      <path
        d="M0 40 Q 50 70 100 60"
        stroke="#c9a84c"
        strokeWidth="1"
        fill="none"
        opacity="0.6"
        transform="translate(5, 20)"
      />
      <circle cx="80" cy="80" r="3" fill="#c9a84c" opacity="0.5" />
      <circle cx="60" cy="60" r="2" fill="#c9a84c" opacity="0.4" />
      <path
        d="M0 0 Q 20 40 50 50 Q 30 20 0 0"
        stroke="#c9a84c"
        strokeWidth="1"
        fill="none"
        opacity="0.4"
        transform="translate(30, 60) rotate(-20)"
      />
      <path
        d="M5 5 C 15 5, 20 15, 20 25 C 20 15, 25 5, 35 5 C 25 5, 20 -5, 20 -15 C 20 -5, 15 5, 5 5Z"
        fill="#c9a84c"
        opacity="0.3"
        transform="translate(90, 40) scale(0.6)"
      />
    </svg>
  );
}

/* ─── Sparkle Particles for envelope screen ─── */
function EnvelopeSparkles() {
  const sparkles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: 2 + Math.random() * 4,
    delay: Math.random() * 5,
    duration: 2 + Math.random() * 3,
  }));

  return (
    <div className="sparkle-container">
      {sparkles.map((s) => (
        <div
          key={s.id}
          className="sparkle"
          style={{
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Floating gold particles background ─── */
function FloatingParticles() {
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    size: 1 + Math.random() * 3,
    delay: Math.random() * 20,
    duration: 15 + Math.random() * 25,
    opacity: 0.2 + Math.random() * 0.4,
  }));

  return (
    <div className="particles-bg">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            boxShadow: `0 0 ${p.size * 2}px rgba(201, 168, 76, ${p.opacity})`,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Countdown Timer ─── */
function Countdown() {
  const targetDate = new Date('2027-01-24T10:00:00').getTime();
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const update = () => {
      const now = Date.now();
      const diff = targetDate - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <div className="countdown-container">
      {[
        { value: timeLeft.days, label: 'Days' },
        { value: timeLeft.hours, label: 'Hours' },
        { value: timeLeft.minutes, label: 'Minutes' },
        { value: timeLeft.seconds, label: 'Seconds' },
      ].map((item) => (
        <div className="countdown-item" key={item.label}>
          <span className="countdown-number">
            {String(item.value ?? 0).padStart(2, '0')}
          </span>
          <span className="countdown-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Main App ─── */
function App() {
  const [envelopeOpened, setEnvelopeOpened] = useState(false);
  const [envelopeHidden, setEnvelopeHidden] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [navVisible, setNavVisible] = useState(false);
  const audioRef = useRef(null);

  /* ─── Envelope open handler ─── */
  const handleEnvelopeClick = useCallback(() => {
    if (envelopeOpened) return;
    setEnvelopeOpened(true);

    // After envelope animation finishes, hide envelope screen
    setTimeout(() => {
      setEnvelopeHidden(true);
      setNavVisible(true);
    }, 2000);

    // Try to play music
    if (audioRef.current) {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // Autoplay blocked, user can use the button
      });
    }
  }, [envelopeOpened]);

  /* ─── Music toggle ─── */
  const toggleMusic = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying]);

  /* ─── Intersection Observer for scroll animations ─── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.fade-in-up');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, [envelopeHidden]);

  /* ─── Smooth scroll to section ─── */
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  /* ─── Dress code colors ─── */
  const dressColors = [
    { color: '#0a1628', label: 'Dark Navy' },
    { color: '#e8dcc8', label: 'Beige' },
  ];

  return (
    <div className="App">
      {/* Hidden audio element — using a royalty-free piano piece URL */}
      <audio
        ref={audioRef}
        loop
        preload="auto"
        src="https://cdn.pixabay.com/audio/2024/11/29/audio_d24b1e0ed0.mp3"
      />

      {/* ═══════════════ ENVELOPE SCREEN ═══════════════ */}
      <div
        className={`envelope-screen ${envelopeHidden ? 'hidden' : ''}`}
        id="envelope-screen"
      >
        <EnvelopeSparkles />
        <div className="floral-bg-left" />
        <div className="floral-bg-right" />

        <div
          className={`envelope-wrapper ${envelopeOpened ? 'opening' : ''}`}
          onClick={handleEnvelopeClick}
          role="button"
          tabIndex={0}
          aria-label="Click to open your wedding invitation"
          onKeyDown={(e) => e.key === 'Enter' && handleEnvelopeClick()}
        >
          <div className="envelope">
            <div className="envelope-border" />
            <div className="envelope-flap" />
            <div className="card-preview">
              <span className="card-preview-text">J & J</span>
            </div>
          </div>
          <div className="wax-seal">
            <span className="seal-initials">J&J</span>
          </div>
        </div>

        <p className="envelope-cta">Click the envelope to open your invitation</p>
      </div>

      {/* ═══════════════ FLOATING PARTICLES ═══════════════ */}
      <FloatingParticles />

      {/* ═══════════════ MUSIC TOGGLE ═══════════════ */}
      {envelopeHidden && (
        <button
          className={`music-toggle ${isPlaying ? 'playing' : ''}`}
          onClick={toggleMusic}
          aria-label={isPlaying ? 'Pause music' : 'Play music'}
          id="music-toggle"
        >
          {isPlaying ? '♫' : '♪'}
        </button>
      )}

      {/* ═══════════════ NAVIGATION ═══════════════ */}
      <nav className={`nav-bar ${navVisible ? 'visible' : ''}`}>
        <button className="nav-btn" onClick={() => scrollTo('details')}>
          💍 Details
        </button>
        <button className="nav-btn" onClick={() => scrollTo('dresscode')}>
          👗 Dress Code
        </button>
        <button className="nav-btn" onClick={() => scrollTo('gifts')}>
          🎁 Gift Guide
        </button>
        <button className="nav-btn" onClick={() => scrollTo('location')}>
          📍 Location
        </button>
        <button className="nav-btn" onClick={() => scrollTo('rsvp')}>
          📸 RSVP
        </button>
      </nav>

      {/* ═══════════════ INVITATION CONTENT ═══════════════ */}
      <main className={`invitation-content ${envelopeHidden ? 'visible' : ''}`}>

        {/* ─── HERO SECTION ─── */}
        <section className="hero-section" id="hero">
          <FloralCorner className="top-left" />
          <FloralCorner className="top-right" />
          <FloralCorner className="bottom-left" />
          <FloralCorner className="bottom-right" />
          <div className="gold-frame" />

          <div className="fade-in-up">
            <div className="hero-ornament-top">✦</div>
          </div>

          <div className="fade-in-up delay-1">
            <p className="hero-label">Together with their families</p>
          </div>

          <div className="fade-in-up delay-2">
            <h1 className="hero-names">
              Jade Reichberg
              <span className="hero-ampersand">&</span>
              Janette
            </h1>
          </div>

          <div className="fade-in-up delay-3">
            <div className="gold-divider" />
            <p className="hero-message">
              Invite you to share our celebration of love as we unite in marriage.
            </p>
          </div>

          <div className="fade-in-up delay-4">
            <div className="hero-ornament-bottom" style={{ marginTop: '30px' }}>✦</div>
          </div>
        </section>

        {/* ─── SAVE THE DATE / DETAILS ─── */}
        <section className="section section-alt" id="details">
          <div className="fade-in-up">
            <span className="section-icon">💍</span>
            <h2 className="section-title">Save the Date</h2>
            <p className="section-subtitle">Wedding Ceremony</p>
          </div>

          <div className="section-content">
            <div className="fade-in-up delay-1">
              <div className="date-display">
                <p className="date-day">Sunday</p>
                <p className="date-main">January 24, 2027</p>
                <p className="date-time">10:00 AM</p>
              </div>
            </div>

            <div className="fade-in-up delay-2">
              <Countdown />
            </div>

            <div className="fade-in-up delay-3">
              <div className="venue-card">
                <p className="venue-label">Ceremony</p>
                <h3 className="venue-name">St. Thomas Aquinas Parish Church</h3>
                <p className="venue-address">
                  Sto. Tomas City, Batangas
                </p>
              </div>
            </div>

            <div className="fade-in-up delay-4">
              <div className="venue-card">
                <p className="venue-label">Reception</p>
                <h3 className="venue-name">Alberto's Event Center</h3>
                <p className="venue-address">
                  J.P. Laurel Street, Poblacion 1<br />
                  (Beside Iglesia ni Cristo)<br />
                  Sto. Tomas City, Batangas
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── DRESS CODE ─── */}
        <section className="section" id="dresscode">
          <div className="fade-in-up">
            <span className="section-icon">👗</span>
            <h2 className="section-title">Dress Code</h2>
            <p className="section-subtitle">Attire Guidelines</p>
          </div>

          <div className="section-content">
            <div className="fade-in-up delay-1">
              <p className="dress-code-text">Semi-Formal Attire</p>
              <p className="dress-code-note">
                Please dress according to our wedding color palette.
              </p>
            </div>

            <div className="fade-in-up delay-2">
              <div className="color-swatches">
                {dressColors.map((c) => (
                  <div
                    className="color-swatch"
                    key={c.label}
                    style={{ backgroundColor: c.color }}
                    title={c.label}
                  >
                    <span className="swatch-label">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── GIFT GUIDE ─── */}
        <section className="section section-alt" id="gifts">
          <div className="fade-in-up">
            <span className="section-icon">🎁</span>
            <h2 className="section-title">Gift Guide</h2>
            <p className="section-subtitle">With Gratitude</p>
          </div>

          <div className="section-content">
            <div className="fade-in-up delay-1">
              <p className="gift-message">
                "Your love, prayers, and presence are the greatest gifts we could receive.
                Should you wish to bless us further, a monetary gift toward our future together
                would be sincerely appreciated."
              </p>
            </div>
          </div>
        </section>

        {/* ─── MAP / LOCATION ─── */}
        <section className="section" id="location">
          <div className="fade-in-up">
            <span className="section-icon">📍</span>
            <h2 className="section-title">Location</h2>
            <p className="section-subtitle">Find Your Way</p>
          </div>

          <div className="section-content">
            <div className="fade-in-up delay-1">
              <div className="venue-card">
                <p className="venue-label">Ceremony</p>
                <h3 className="venue-name">St. Thomas Aquinas Parish Church</h3>
                <p className="venue-address">Sto. Tomas City, Batangas</p>
              </div>
            </div>

            <div className="fade-in-up delay-2">
              <div className="map-container">
                <iframe
                  title="St. Thomas Aquinas Parish Church Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3873.8!2d121.1413!3d14.1067!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd6e7a4d9b2c45%3A0x2!2sSt.+Thomas+Aquinas+Parish+Church%2C+Sto.+Tomas%2C+Batangas!5e0!3m2!1sen!2sph!4v1700000000000"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            <div className="fade-in-up delay-3">
              <div className="venue-card" style={{ marginTop: '30px' }}>
                <p className="venue-label">Reception</p>
                <h3 className="venue-name">Alberto's Event Center</h3>
                <p className="venue-address">
                  J.P. Laurel Street, Poblacion 1<br />
                  (Beside Iglesia ni Cristo)<br />
                  Sto. Tomas City, Batangas
                </p>
              </div>
            </div>

            <div className="fade-in-up delay-4">
              <a
                className="directions-btn"
                href="https://www.google.com/maps/dir/?api=1&destination=St.+Thomas+Aquinas+Parish+Church+Sto.+Tomas+Batangas"
                target="_blank"
                rel="noopener noreferrer"
              >
                📍 Get Directions
              </a>
            </div>
          </div>
        </section>

        {/* ─── RSVP ─── */}
        <section className="section rsvp-section" id="rsvp">
          <div className="fade-in-up">
            <span className="section-icon">💌</span>
            <h2 className="section-title">RSVP</h2>
            <p className="section-subtitle">Kindly Respond</p>
          </div>

          <div className="section-content">
            <div className="fade-in-up delay-1">
              <p className="rsvp-text">
                As much as we would love to celebrate with everyone, seating is limited.<br />
                Kindly confirm your attendance by sending us a direct message.
              </p>
            </div>
          </div>
        </section>

        {/* ─── CLOSING ─── */}
        <section className="closing-section" id="closing">
          <FloralCorner className="top-left" />
          <FloralCorner className="top-right" />
          <FloralCorner className="bottom-left" />
          <FloralCorner className="bottom-right" />

          <div className="fade-in-up">
            <p className="closing-message">
              "We can't wait to celebrate our special day with you.
              Thank you for being part of our forever."
            </p>
          </div>

          <div className="fade-in-up delay-1">
            <h2 className="closing-names">Jade & Janette</h2>
            <div className="gold-flourish" />
          </div>

          <div className="fade-in-up delay-2">
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '11px',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              marginTop: '30px',
              opacity: 0.5,
            }}>
              January 24, 2027
            </p>
          </div>
        </section>
      </main>

    </div>
  );
}

export default App;
